/**
 * Episode Testing API Client with React Query Hooks
 *
 * Provides type-safe API client for Episode Evaluation & Testing System using:
 * - Zod for runtime validation
 * - React Query for data fetching and caching
 * - Proper error handling and transformations
 *
 * Story: 11.7 - Episode Evaluation & Testing System
 * Story: 24.2 - Evolve Single Episode Eval (simulation endpoint)
 */

import { useQuery, useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPost } from 'services/api';
import logger from 'utils/logger';

// ============================================================================
// STEP 1: Define Zod Schemas (camelCase for frontend)
// ============================================================================

// Conversation Message Schema
const ConversationMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string().max(5000),
});

// Tool Call Record Schema
const ToolCallRecordSchema = z.object({
  toolName: z.string(),
  arguments: z.record(z.unknown()).nullable().optional(),
  afterTurn: z.number().int().min(0),
});

// Criteria Score Schema (with evidence)
const CriteriaScoreSchema = z.object({
  score: z.number().min(0).max(1),
  evidence: z.array(z.string()),
});

// Violation Schema
const ViolationSchema = z.object({
  rule: z.string(),
  evidence: z.string(),
});

// Episode Testing Data Response Schema
// Story 15.8: tagIds are now integers (migrated from UUID)
const EpisodeTestingDataResponseSchema = z.object({
  episodeId: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  context: z.string(),
  goals: z.array(z.string()),
  personas: z.array(
    z.object({
      name: z.string(),
      description: z.string(),
    })
  ),
  scenario: z.string(),
  language: z.string(),
  complexityLevel: z.number().int().min(1).max(5),
  durationId: z.number().int().min(1).max(5),
  tagIds: z.array(z.number().int()),
});

// Persona Turn Schema
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PersonaTurnSchema = z.object({
  persona: z.string(),
  message: z.string(),
});

// Run Simulation Request Schema
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RunSimulationRequestSchema = z.object({
  episodeId: z.string().uuid(),
  language: z.string(),
  personaInstructions: z.string().min(10),
  maxTurns: z.number().int().min(1).max(50).default(50),
  promptOverride: z.string().nullable().optional(),
  contextOverride: z.string().nullable().optional(),
  scenarioOverride: z.string().nullable().optional(),
  goalsOverride: z.array(z.string()).nullable().optional(),
  personasOverride: z.array(z.record(z.unknown())).nullable().optional(),
  complexityLevelOverride: z.number().int().min(1).max(5).nullable().optional(),
});

// Simulation Result Response Schema
const SimulationResultSchema = z.object({
  conversationHistory: z.array(ConversationMessageSchema),
  toolCalls: z.array(ToolCallRecordSchema),
  exchangeCount: z.number().int().min(0),
  completionReason: z.enum(['episode_complete', 'goals_completed', 'max_turns', 'error']),
  episodeComplete: z.boolean(),
});

// Evaluate Conversation Request Schema
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EvaluateConversationRequestSchema = z.object({
  episodeId: z.string().uuid(),
  language: z.string(),
  conversationHistory: z.array(ConversationMessageSchema).min(1),
  toolCalls: z.array(ToolCallRecordSchema).default([]),
  promptText: z.string().nullable().optional(),
  contextOverride: z.string().nullable().optional(),
  scenarioOverride: z.string().nullable().optional(),
  goalsOverride: z.array(z.string()).nullable().optional(),
  personasOverride: z.array(z.record(z.unknown())).nullable().optional(),
  complexityLevelOverride: z.number().int().min(1).max(5).nullable().optional(),
});

// Evaluate Conversation Response Schema
const EvaluateConversationResponseSchema = z.object({
  overallScore: z.number().min(0).max(1),
  decision: z.enum(['PASS', 'NEEDS_IMPROVEMENT']),
  summary: z.string(),
  criteriaScores: z.record(z.string(), CriteriaScoreSchema),
  disallowedBehaviorViolations: z.array(ViolationSchema).default([]),
  recommendations: z.array(z.string()),
  strengths: z.array(z.string()),
});

// ============================================================================
// Content Judge Schemas (Story 24.4)
// ============================================================================

const ContentScoreSchema = z.object({
  score: z.number().int().min(1).max(10),
  reasoning: z.string(),
});

const ContentJudgeResultSchema = z.object({
  overallScore: z.number().int().min(1).max(10),
  goalClarity: ContentScoreSchema,
  goalCefrFit: ContentScoreSchema,
  scenarioEngagement: ContentScoreSchema,
  personaDifferentiation: ContentScoreSchema,
  contextClarity: ContentScoreSchema,
  strengths: z.array(z.string()),
  weaknesses: z.array(z.string()),
});

// ============================================================================
// Judge Summarizer Schemas (Story 24.4)
// ============================================================================

const PersonaScoreSummarySchema = z.object({
  personaName: z.string(),
  overallScore: z.number().int(),
  keyConcern: z.string().nullable(),
});

const JudgeSummaryResultSchema = z.object({
  unifiedScore: z.number(),
  allPassed: z.boolean(),
  minScore: z.number().int(),
  maxScore: z.number().int(),
  consensusStrengths: z.array(z.string()),
  consensusWeaknesses: z.array(z.string()),
  improvementDirectives: z.array(z.string()),
  perPersonaSummary: z.array(PersonaScoreSummarySchema),
});

// ============================================================================
// Episode Improvement Schemas (Story 24.3 → 24.4)
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IterationRecordSchema = z.object({
  iteration: z.number().int(),
  changesMade: z.record(z.unknown()),
  rationale: z.string(),
  scores: z.record(z.number()),
});

const EpisodeImprovementProposalSchema = z.object({
  proposedChanges: z.record(z.unknown()),
  rationale: z.string(),
  fieldRationales: z.record(z.string()),
  expectedImprovements: z.array(z.string()),
});

// Export TypeScript types from Zod schemas
export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;
export type ToolCallRecord = z.infer<typeof ToolCallRecordSchema>;
export type CriteriaScore = z.infer<typeof CriteriaScoreSchema>;
export type Violation = z.infer<typeof ViolationSchema>;
export type PersonaTurn = z.infer<typeof PersonaTurnSchema>;
export type EpisodeTestingData = z.infer<typeof EpisodeTestingDataResponseSchema>;
export type RunSimulationRequest = z.infer<typeof RunSimulationRequestSchema>;
export type SimulationResult = z.infer<typeof SimulationResultSchema>;
export type EvaluateConversationRequest = z.infer<typeof EvaluateConversationRequestSchema>;
export type EvaluateConversationResponse = z.infer<typeof EvaluateConversationResponseSchema>;
export type ContentScore = z.infer<typeof ContentScoreSchema>;
export type ContentJudgeResult = z.infer<typeof ContentJudgeResultSchema>;
export type PersonaScoreSummary = z.infer<typeof PersonaScoreSummarySchema>;
export type JudgeSummaryResult = z.infer<typeof JudgeSummaryResultSchema>;
export type IterationRecord = z.infer<typeof IterationRecordSchema>;
export type EpisodeImprovementProposal = z.infer<typeof EpisodeImprovementProposalSchema>;

// ============================================================================
// STEP 2: Transform Functions (snake_case <-> camelCase)
// ============================================================================

/**
 * Transform snake_case API response to camelCase frontend types
 */
function transformSnakeToCamel<T>(obj: Record<string, unknown>): T {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    if (Array.isArray(value)) {
      transformed[camelKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformSnakeToCamel(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[camelKey] = transformSnakeToCamel(value as Record<string, unknown>);
    } else {
      transformed[camelKey] = value;
    }
  }

  return transformed as T;
}

/**
 * Transform camelCase frontend types to snake_case API request
 */
function transformCamelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const transformed: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Convert camelCase to snake_case
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

    if (Array.isArray(value)) {
      transformed[snakeKey] = value.map((item) =>
        typeof item === 'object' && item !== null
          ? transformCamelToSnake(item as Record<string, unknown>)
          : item
      );
    } else if (typeof value === 'object' && value !== null) {
      transformed[snakeKey] = transformCamelToSnake(value as Record<string, unknown>);
    } else {
      transformed[snakeKey] = value;
    }
  }

  return transformed;
}

// ============================================================================
// STEP 3: Define Fetcher Functions
// ============================================================================

/**
 * Fetch episode data for testing
 * GET /api/admin/episodes/:episode_id/for-eval?language={language}
 */
const fetchEpisodeForTesting = async (
  episodeId: string,
  language?: string
): Promise<EpisodeTestingData> => {
  try {
    const params = language ? { language } : undefined;
    const data = await apiGet<unknown>(`/api/admin/episodes/${episodeId}/for-eval`, params);
    return EpisodeTestingDataResponseSchema.parse(
      transformSnakeToCamel(data as Record<string, unknown>)
    );
  } catch (error) {
    logger.error('Error fetching episode for testing:', error);
    throw error;
  }
};

/**
 * Run a full episode simulation
 * POST /api/admin/episodes/eval/run-simulation
 */
const runSimulation = async (request: RunSimulationRequest): Promise<SimulationResult> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPost<unknown>(
      '/api/admin/episodes/eval/run-simulation',
      payload,
      180000 // 180s timeout — simulations can take 30-120s
    );
    return SimulationResultSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error running simulation:', error);
    throw error;
  }
};

/**
 * Evaluate conversation with Judge
 * POST /api/admin/episodes/eval/evaluate
 */
const evaluateConversation = async (
  request: EvaluateConversationRequest
): Promise<EvaluateConversationResponse> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPost<unknown>('/api/admin/episodes/eval/evaluate', payload, 180000);
    return EvaluateConversationResponseSchema.parse(
      transformSnakeToCamel(data as Record<string, unknown>)
    );
  } catch (error) {
    logger.error('Error evaluating conversation:', error);
    throw error;
  }
};

/**
 * Evaluate episode content quality with content judge
 * POST /api/admin/episodes/eval/content-judge
 */
const contentJudge = async (request: {
  language: string;
  episodeContext: string;
  episodeScenario: string;
  episodeGoals: string[];
  episodePersonas: Array<Record<string, unknown>>;
  complexityLevel: number;
  conversationHistory: ConversationMessage[];
  toolCalls: ToolCallRecord[];
  evalPersonaName: string;
  evalPersonaDescription: string;
}): Promise<ContentJudgeResult> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPost<unknown>('/api/admin/episodes/eval/content-judge', payload, 180000);
    return ContentJudgeResultSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error running content judge:', error);
    throw error;
  }
};

/**
 * Aggregate multiple content judge results into unified verdict
 * POST /api/admin/episodes/eval/summarize-judges
 */
const summarizeJudges = async (request: {
  language: string;
  episodeContext: string;
  episodeScenario: string;
  episodeGoals: string[];
  episodePersonas: Array<Record<string, unknown>>;
  complexityLevel: number;
  judgeResults: Array<{
    evalPersonaName: string;
    evalPersonaCefr: string | null;
    judgeResult: ContentJudgeResult;
  }>;
  iterationHistory: Array<{
    iteration: number;
    changesApplied: string;
    scoresBefore: Record<string, number>;
    scoresAfter: Record<string, number>;
  }>;
}): Promise<JudgeSummaryResult> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPost<unknown>(
      '/api/admin/episodes/eval/summarize-judges',
      payload,
      180000
    );
    return JudgeSummaryResultSchema.parse(transformSnakeToCamel(data as Record<string, unknown>));
  } catch (error) {
    logger.error('Error summarizing judges:', error);
    throw error;
  }
};

/**
 * Propose episode improvements based on judge summary
 * POST /api/admin/episodes/eval/improve-episode
 */
const improveEpisode = async (request: {
  episodeContext: string;
  episodeScenario: string;
  episodeGoals: string[];
  episodePersonas: Array<Record<string, unknown>>;
  complexityLevel: number;
  languageCode: string;
  judgeSummary: JudgeSummaryResult;
  iterationHistory: IterationRecord[];
}): Promise<EpisodeImprovementProposal> => {
  try {
    const payload = transformCamelToSnake(request as unknown as Record<string, unknown>);
    const data = await apiPost<unknown>(
      '/api/admin/episodes/eval/improve-episode',
      payload,
      180000
    );
    return EpisodeImprovementProposalSchema.parse(
      transformSnakeToCamel(data as Record<string, unknown>)
    );
  } catch (error) {
    logger.error('Error improving episode:', error);
    throw error;
  }
};

// ============================================================================
// STEP 4: Define React Query Hooks
// ============================================================================

/**
 * Fetch episode for testing
 * Hook: useFetchEpisodeForTesting
 */
export const useFetchEpisodeForTesting = (episodeId: string | undefined, language?: string) => {
  return useQuery({
    queryKey: ['episode-testing', episodeId, language],
    queryFn: () => fetchEpisodeForTesting(episodeId!, language),
    enabled: !!episodeId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Run full simulation
 * Hook: useRunSimulation
 */
export const useRunSimulation = () => {
  return useMutation({
    mutationFn: runSimulation,
  });
};

/**
 * Evaluate conversation
 * Hook: useEvaluateConversation
 */
export const useEvaluateConversation = () => {
  return useMutation({
    mutationFn: evaluateConversation,
  });
};

/**
 * Content judge evaluation
 * Hook: useContentJudge
 */
export const useContentJudge = () => {
  return useMutation({
    mutationFn: contentJudge,
  });
};

/**
 * Summarize multiple judge results
 * Hook: useSummarizeJudges
 */
export const useSummarizeJudges = () => {
  return useMutation({
    mutationFn: summarizeJudges,
  });
};

/**
 * Improve episode
 * Hook: useImproveEpisode
 */
export const useImproveEpisode = () => {
  return useMutation({
    mutationFn: improveEpisode,
  });
};
